import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { PageHeader, ViewSwitch, ViewsPanel, initialsOf } from "@/components/patterns";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { cn } from "@/lib/utils";
import { Loader2, Paperclip, Reply, Search, X } from "lucide-react";
import { StatusBadge, UnlinkedEmails } from "./MailboxSettings";
import { ConnectionTab, TeamMailboxesTab, SetupGuideTab } from "@/pages/settings/EmailSettingsPage";
import { ComposeEmailDialog } from "@/pages/deals/ComposeEmailDialog";
import { EmailMessage, formatEmailDate, groupEmailThreads, replyDefaults } from "@/pages/deals/DealEmailThread";
import type { DealEmailRecord, EmailCounts, InboxThread, MailboxInfo, ThreadPage } from "@/types/email";
import { dateLocale, withClock } from "@/lib/dateTime";

type TabKey = "inbox" | "settings" | "team" | "guide";
type Folder = "inbox" | "needs_reply" | "unread" | "sent" | "unlinked";
type ThreadEmail = DealEmailRecord & { deal?: { id: string; name: string } };

const DEAL_TAG_RE = /\s*\[Deal #[0-9a-f]{8}\]/i;
const cleanSubject = (s: string) => (s || "(no subject)").replace(DEAL_TAG_RE, "").trim() || "(no subject)";

/** "16:05" today, "Yesterday", weekday this week, else date */
const listTime = (v: string) => {
  const d = new Date(v);
  if (d.toDateString() === new Date().toDateString()) return d.toLocaleTimeString(dateLocale(), withClock({ hour: "2-digit", minute: "2-digit" }));
  if (d.toDateString() === new Date(Date.now() - 86400000).toDateString()) return "Yesterday";
  if (Date.now() - d.getTime() < 6 * 86400000) return d.toLocaleDateString(dateLocale(), { weekday: "short" });
  return d.toLocaleDateString(dateLocale(), { day: "numeric", month: "short" });
};

export function EmailsPage() {
  const { hasPermission, user } = useAuth();
  const [params, setParams] = useSearchParams();
  const canSend = hasPermission("email.send");
  const canSeeTeam = hasPermission("mailbox.admin");
  const roleCode = String(user?.UserRole?.RoleCode || "").toUpperCase();

  const rawRequested = (params.get("tab") || "inbox") as string;
  const requested: TabKey = rawRequested === "connection" ? "settings" : rawRequested === "unlinked" ? "inbox" : (rawRequested as TabKey);
  const tab: TabKey = (requested === "settings" && !canSend) || (requested === "team" && !canSeeTeam) || (requested === "guide" && !canSend) ? "inbox" : requested;
  const setTab = (key: TabKey) => setParams(key === "inbox" ? {} : { tab: key }, { replace: true });

  const [mailbox, setMailbox] = useState<MailboxInfo | null>(null);
  const [mailboxLoaded, setMailboxLoaded] = useState(false);
  const [counts, setCounts] = useState<EmailCounts>({ unread: 0, unlinked: 0 });
  const [folder, setFolder] = useState<Folder>(rawRequested === "unlinked" ? "unlinked" : "inbox");

  const loadCounts = useCallback(() => {
    api.get("/api/DealEmail/Counts").then((res) => setCounts(unwrapObject<EmailCounts>(res, { unread: 0, unlinked: 0 }))).catch(() => undefined);
  }, []);
  useEffect(loadCounts, [loadCounts]);

  const loadMailbox = useCallback(() => {
    if (!canSend) return setMailboxLoaded(true);
    api
      .get("/api/Mailbox/Me")
      .then((res) => setMailbox(unwrapObject<{ mailbox: MailboxInfo | null }>(res)?.mailbox || null))
      .catch(() => setMailbox(null))
      .finally(() => setMailboxLoaded(true));
  }, [canSend]);
  useEffect(() => {
    loadMailbox();
  }, [loadMailbox]);

  const tabs: { key: TabKey; label: string; show: boolean }[] = [
    { key: "inbox", label: "Mail", show: true },
    { key: "settings", label: "Connection", show: canSend },
    { key: "team", label: roleCode === "SALES_HEAD" ? "Team mailboxes" : "All mailboxes", show: canSeeTeam },
    { key: "guide", label: "Setup guide", show: canSend },
  ];
  const visibleTabs = tabs.filter((t) => t.show);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {tab === "inbox" && (
        <ViewsPanel
          title="Email"
          subtitle={roleCode === "SUPER_ADMIN" ? "Every deal in the company" : roleCode === "SALES_HEAD" ? "Your deals and your team's" : "Your deals"}
          active={folder}
          onSelect={(k) => setFolder(k as Folder)}
          groups={[
            {
              items: [
                { key: "inbox", label: "Inbox", count: counts.inbox },
                { key: "needs_reply", label: "Needs reply", count: counts.needs_reply, tone: "warning" },
                { key: "unread", label: "Unread", count: counts.unread || undefined },
                { key: "sent", label: "Sent", count: counts.sent },
                { key: "unlinked", label: "Not linked to a deal", count: counts.unlinked, tone: "warning" },
              ],
            },
          ]}
          footer={
            canSend && mailboxLoaded ? (
              <button type="button" onClick={() => setTab("settings")} className="mx-2.5 mt-4 flex flex-col gap-1 rounded-lg border border-border bg-card px-3 py-2.5 text-left hover:bg-muted/50">
                <span className="text-[13px] font-semibold">Your mailbox</span>
                <span className="truncate text-xs text-muted-foreground">{mailbox?.email_address || "Not connected"}</span>
                <StatusBadge mailbox={mailbox} />
                <span className="text-[11.5px] text-muted-foreground">Only mail with customers on your deals is shown here.</span>
              </button>
            ) : undefined
          }
        />
      )}

      <PageHeader title="Email" switcher={visibleTabs.length > 1 ? <ViewSwitch value={tab} options={visibleTabs.map(({ key, label }) => ({ key, label }))} onChange={setTab} label="Email sections" /> : undefined} />

      {tab === "inbox" && (
        <>
          {canSend && mailboxLoaded && (!mailbox || mailbox.sync_status === "AUTH_FAILED") && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning/30 bg-warning-soft px-4 py-2.5 text-sm text-warning">
              <span>{mailbox ? "Your mailbox login failed, so new replies are not coming in." : "Connect your mailbox to send emails from deals and receive customer replies here."}</span>
              <Button size="sm" onClick={() => setTab("settings")}>
                {mailbox ? "Reconnect" : "Connect mailbox"}
              </Button>
            </div>
          )}
          {folder === "unlinked" ? <UnlinkedEmails onChanged={loadCounts} /> : <Mail folder={folder} mailbox={mailbox} canReply={canSend} onChanged={loadCounts} />}
        </>
      )}
      {tab === "settings" && canSend && <ConnectionTab mailbox={mailbox} onUpdated={loadMailbox} />}
      {tab === "team" && canSeeTeam && <TeamMailboxesTab />}
      {tab === "guide" && canSend && <SetupGuideTab onSelectPreset={() => setTab("settings")} />}
    </div>
  );
}

/** Conversation list on the left, the open conversation on the right */
function Mail({ folder, mailbox, canReply, onChanged }: { folder: Folder; mailbox: MailboxInfo | null; canReply: boolean; onChanged: () => void }) {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<ThreadPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => (setQuery(search.trim()), setPage(1)), 350);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => setPage(1), [folder]);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams({ view: folder, page: String(page) });
    if (query) qs.set("search", query);
    api
      .get(`/api/DealEmail/Threads?${qs}`)
      .then((res) => setData(unwrapObject<ThreadPage>(res, { rows: [], total: 0, page: 1, page_size: 30 })))
      .catch(() => setData({ rows: [], total: 0, page: 1, page_size: 30 }))
      .finally(() => setLoading(false));
  }, [folder, query, page, version]);

  const reload = () => {
    setVersion((v) => v + 1);
    onChanged();
  };
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="flex min-h-[600px] flex-1 overflow-hidden rounded-lg border border-border bg-card">
      <section aria-label="Conversations" className={cn("min-h-0 w-full shrink-0 flex-col border-r border-border md:w-[340px]", openId ? "hidden md:flex" : "flex")}>
        <div className="flex items-center gap-2 border-b border-border p-3">
          <label className="flex h-[30px] flex-1 items-center gap-2 rounded-lg border border-border bg-card px-2.5 text-muted-foreground">
            <Search className="size-3.5 shrink-0" />
            <span className="sr-only">Search mail</span>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search subject, sender or deal" className="w-full bg-transparent text-xs text-foreground outline-none placeholder:text-muted-foreground" />
            {search && (
              <button type="button" onClick={() => setSearch("")} aria-label="Clear search">
                <X className="size-3.5" />
              </button>
            )}
          </label>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading && !data ? (
            <div className="flex justify-center py-10">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          ) : !data || data.rows.length === 0 ? (
            <div className="px-6 py-12 text-center text-[13px] text-muted-foreground">
              {query ? "No conversations match." : folder === "needs_reply" ? "Everyone has been answered." : "No email here yet. Send one from a deal and customer replies will show up here."}
            </div>
          ) : (
            data.rows.map((t) => <ThreadRow key={t.thread_id} t={t} active={openId === t.thread_id} onOpen={() => setOpenId(t.thread_id)} />)
          )}
        </div>
        {data && data.total > data.page_size && (
          <div className="flex items-center justify-between border-t border-border px-3 py-2 text-[11.5px] text-muted-foreground">
            <span>
              {data.total} · page {page} of {totalPages}
            </span>
            <span className="flex gap-1.5">
              <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </Button>
              <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </span>
          </div>
        )}
      </section>

      {openId ? (
        <Reading key={openId} threadId={openId} mailbox={mailbox} canReply={canReply} onBack={() => setOpenId(null)} onChanged={reload} />
      ) : (
        <div className="hidden flex-1 flex-col items-center justify-center gap-1 bg-muted/30 px-10 text-center md:flex">
          <span className="text-base font-semibold">Pick a conversation</span>
          <span className="max-w-sm text-sm text-muted-foreground">Emails with customers on your deals, grouped as conversations. Ones waiting for your answer are in “Needs reply”.</span>
        </div>
      )}
    </div>
  );
}

function ThreadRow({ t, active, onOpen }: { t: InboxThread; active: boolean; onOpen: () => void }) {
  const unread = t.thread_unread > 0;
  const who = t.direction === "IN" ? t.from_name || t.from_address : `To ${t.to_addresses.join(", ")}`;
  return (
    <button type="button" onClick={onOpen} className={cn("flex w-full flex-col gap-0.5 border-b border-border/70 border-l-[3px] px-3.5 py-2.5 text-left hover:bg-muted/50", active ? "border-l-primary bg-accent/40" : unread ? "border-l-transparent bg-primary/5" : "border-l-transparent")}>
      <span className="flex items-baseline justify-between gap-2">
        <span className={cn("truncate text-[13px]", unread ? "font-bold" : "font-medium")}>{who}</span>
        <span className="shrink-0 font-mono text-[11px] text-muted-foreground">{listTime(t.email_date)}</span>
      </span>
      <span className={cn("truncate text-[13px]", unread ? "font-semibold" : "")}>
        {cleanSubject(t.subject)}
        {t.thread_emails > 1 && <span className="ml-1.5 rounded-full bg-muted px-1.5 text-[10.5px] font-normal text-muted-foreground">{t.thread_emails}</span>}
      </span>
      <span className="truncate text-xs text-muted-foreground">{t.snippet}</span>
      <span className="mt-0.5 flex items-center gap-1.5">
        <span className="max-w-[170px] truncate rounded bg-accent px-1.5 text-[10.5px] font-semibold leading-[18px] text-accent-foreground">{t.deal.name}</span>
        {t.needs_reply && <span className="rounded bg-warning-soft px-1.5 text-[10.5px] font-semibold leading-[18px] text-warning">Needs reply</span>}
        {t.attachments.length > 0 && <Paperclip className="size-3 text-muted-foreground" />}
        {unread && <span className="ml-auto size-2 rounded-full bg-primary" aria-label="Unread" />}
      </span>
    </button>
  );
}

/** One conversation: its emails, the deal it belongs to, and a way to answer */
function Reading({ threadId, mailbox, canReply, onBack, onChanged }: { threadId: string; mailbox: MailboxInfo | null; canReply: boolean; onBack: () => void; onChanged: () => void }) {
  const [emails, setEmails] = useState<ThreadEmail[] | null>(null);
  const [replyOpen, setReplyOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);

  const load = useCallback(() => {
    api
      .get(`/api/DealEmail/Thread/${threadId}`)
      .then((res) => setEmails(unwrapArray<ThreadEmail>(res)))
      .catch(() => setEmails([]));
  }, [threadId]);
  useEffect(load, [load]);

  // Opening a conversation reads it
  useEffect(() => {
    api.patch(`/api/DealEmail/MarkThreadRead/${threadId}`).then(() => onChanged()).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  if (!emails) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (emails.length === 0) return <div className="flex-1 p-8 text-sm text-muted-foreground">This conversation is not available.</div>;

  const thread = groupEmailThreads(emails)[0]!;
  const deal = emails.find((e) => e.deal)?.deal;
  const last = thread.last;
  const needsReply = last.direction === "IN";

  return (
    <section aria-label="Conversation" className="flex min-h-0 min-w-0 flex-1 flex-col">
      <header className="flex flex-col gap-2 border-b border-border px-5 py-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-2">
            <button type="button" onClick={onBack} className="mt-0.5 text-sm text-primary md:hidden">
              ‹ Back
            </button>
            <h2 className="text-[17px] font-semibold leading-snug">{thread.subject}</h2>
          </div>
          <div className="flex gap-1.5">
            {canReply && (
              <Button size="sm" onClick={() => setReplyOpen(true)}>
                <Reply className="mr-1.5 size-3.5" />
                Reply
              </Button>
            )}
            {deal && (
              <Button size="sm" variant="outline" onClick={() => setTaskOpen(true)}>
                Add task
              </Button>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {deal && (
            <>
              <span className="text-muted-foreground">Linked to</span>
              <Link to={`/deals/${deal.id}`} className="rounded-md bg-accent px-2 py-0.5 font-semibold text-accent-foreground hover:underline">
                Deal · {deal.name}
              </Link>
            </>
          )}
          {needsReply && <span className="rounded bg-warning-soft px-1.5 font-semibold leading-5 text-warning">Waiting for your reply since {formatEmailDate(last.email_date)}</span>}
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto bg-muted/30 p-4">
        {thread.messages.map((m) => (
          <EmailMessage key={m.id} email={m} />
        ))}
      </div>

      {canReply && deal && (
        <>
          <ComposeEmailDialog open={replyOpen} onOpenChange={setReplyOpen} dealId={deal.id} dealName={deal.name} defaults={replyDefaults(thread, mailbox?.email_address)} onSent={() => (setReplyOpen(false), load(), onChanged())} />
        </>
      )}
      {deal && <TaskDialog open={taskOpen} onOpenChange={setTaskOpen} defaultRelated={{ type: "DEAL", id: deal.id, name: deal.name }} onSaved={() => setTaskOpen(false)} />}
    </section>
  );
}

export default EmailsPage;
